import React, { useEffect, useState } from "react";
import { useAuthInfo } from "@propelauth/react";
import { fetchWithToken, postWithToken } from "../../api/apiBase";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card } from "../ui/card";
import { useToast } from "../ui/use-toast";
import { UserPlus, Check, X } from "lucide-react";
import {ProjectContext} from "../../contexts/ProjectContext";
import {useParams} from "react-router-dom";

interface JoinRequestItem {
    _id: string;
    user_email: string;
    user_name: string | null;
    company_name: string | null;
    project_id: string;
    status: string;
    created_at: string;
}

interface Props {
    project_id: string;
}

export default function JoinProjectRequestList() {
    const { accessToken } = useAuthInfo();
    const { toast } = useToast();
    // const projectUserContext = React.useContext(ProjectContext);
    const [requests, setRequests] = useState<JoinRequestItem[]>([]);
    const [responding, setResponding] = useState<string | null>(null);
    const {project_id, scope_id} = useParams<string>();

    const fetchRequests = async () => {
        if (!accessToken || !project_id) return;
        try {
            const data = await fetchWithToken(
                `/join-requests/project/${project_id}`,
                accessToken
            );
            setRequests(data);
        } catch (err) {
            // silently fail — developer may not have access
        }
    };

    useEffect(() => {
        fetchRequests();
    }, [project_id]);

    const handleRespond = async (requestId: string, approved: boolean) => {
        if (!accessToken) return;
        setResponding(requestId);
        console.log({ request_id: requestId, approved:approved })
        postWithToken(
                "/join-requests/respond",
                JSON.stringify({ request_id: requestId, approved:approved }),
                accessToken
            ).then((x) => {
                setRequests((prev) => prev.filter((r) => r._id !== requestId));
                toast({
                    title: approved ? "Request approved" : "Request denied",
                    description: approved
                        ? "User has been granted access to the project."
                        : "Request has been denied.",
                });
            }).catch( (err)  => {
                console.log(err)
                toast({
                    title: "Error",
                    description: "Failed to respond to request. " + err as string ,
                    variant: "destructive",
                });
            }).finally(() =>  {
                setResponding(null);
            })
    };

    const formatDate = (dateStr: string) => {
        try {
            return new Date(dateStr).toLocaleDateString();
        } catch {
            return "";
        }
    };

    return (
        <Dialog onOpenChange={(open) => { if (open) fetchRequests(); }}>
            <DialogTrigger asChild>
                <Button variant="outline" className="relative">
                    <UserPlus className="h-4 w-4 mr-1" />
                    Join Requests
                    {requests.length > 0 && (
                        <Badge variant="warning" className="ml-2 px-1.5 py-0 text-xs">
                            {requests.length}
                        </Badge>
                    )}
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogTitle className="font-normal !text-[17px] mb-2">
                    Join Requests
                </DialogTitle>

                {requests.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4">No pending requests.</p>
                ) : (
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                        {requests.map((req) => (
                            <Card key={req._id} className="p-3">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="font-medium text-sm">
                                            {req.user_name || req.user_email}
                                        </div>
                                        {req.user_name && (
                                            <div className="text-xs text-gray-500">{req.user_email}</div>
                                        )}
                                        {req.company_name && (
                                            <div className="text-xs text-gray-400">{req.company_name}</div>
                                        )}
                                        <div className="text-xs text-gray-400 mt-1">
                                            {formatDate(req.created_at)}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            variant="default"
                                            disabled={responding === req._id}
                                            onClick={() => handleRespond(req._id, true)}
                                        >
                                            <Check className="h-3 w-3 mr-1" />
                                            Approve
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={responding === req._id}
                                            onClick={() => handleRespond(req._id, false)}
                                        >
                                            <X className="h-3 w-3 mr-1" />
                                            Deny
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
